import { useState, useMemo } from 'react'
import { deleteAircraft } from '../lib/api'
import { supabase } from '../lib/supabase'
import AircraftModal from './AircraftModal'

function groupAircraft(aircraft) {
  const tree = {}
  aircraft.forEach(a => {
    const mfr = a.manufacturer || 'Unknown Manufacturer'
    const model = a.model || 'Unknown Model'
    if (!tree[mfr]) tree[mfr] = {}
    if (!tree[mfr][model]) tree[mfr][model] = []
    tree[mfr][model].push(a)
  })
  return tree
}

export default function AircraftRegistry({ aircraft, refetch, onClose, isEditor, asPage = false }) {
  const [showModal, setShowModal] = useState(false)
  const [editAircraft, setEditAircraft] = useState(null)

  const handleEdit = (a) => { setEditAircraft(a); setShowModal(true) }
  const handleAdd  = ()  => { setEditAircraft(null); setShowModal(true) }
  const [expandedMfr, setExpandedMfr] = useState(new Set())
  const [expandedModel, setExpandedModel] = useState(new Set())
  const [expandedReg, setExpandedReg] = useState(new Set())
  const [search, setSearch] = useState('')

  const handleDeleteAircraft = async (id) => {
    if (!confirm('Delete this aircraft? Projects linked to it will be unlinked.')) return
    await deleteAircraft(id)
    refetch()
  }

  const toggleMfr = (mfr) => setExpandedMfr(prev => {
    const next = new Set(prev)
    next.has(mfr) ? next.delete(mfr) : next.add(mfr)
    return next
  })

  const toggleModel = (key) => setExpandedModel(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const toggleReg = (id) => setExpandedReg(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  // Filter aircraft by search
  const filtered = useMemo(() => {
    if (!search) return aircraft
    const q = search.toLowerCase()
    return aircraft.filter(a =>
      (a.registration || '').toLowerCase().includes(q) ||
      (a.manufacturer || '').toLowerCase().includes(q) ||
      (a.model || '').toLowerCase().includes(q) ||
      (a.serial_number || '').toLowerCase().includes(q)
    )
  }, [aircraft, search])

  // Auto-expand if searching
  const tree = useMemo(() => groupAircraft(filtered), [filtered])

  // When searching, auto-expand everything
  const effectiveExpandedMfr = search ? new Set(Object.keys(tree)) : expandedMfr
  const effectiveExpandedModel = search
    ? new Set(Object.entries(tree).flatMap(([mfr, models]) => Object.keys(models).map(m => `${mfr}__${m}`)))
    : expandedModel

  const content = (
    <>
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 ${asPage ? '' : ''}`}>
        <h2 className="text-base font-semibold text-gray-900">Aircraft Registry</h2>
        <div className="flex items-center gap-2">
          {isEditor && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Aircraft
            </button>
          )}
          {!asPage && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-6 py-3 border-b border-gray-100">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search registration, manufacturer, model, serial..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </div>

      {/* Tree */}
      <div className={`${asPage ? '' : 'flex-1 '}overflow-y-auto px-4 py-2`}>
          {Object.keys(tree).length === 0 && (
            <div className="text-center text-gray-400 text-sm py-12">
              {search ? 'No aircraft match your search' : 'No aircraft yet — click Add Aircraft to get started'}
            </div>
          )}

          {Object.entries(tree).map(([mfr, models]) => {
            const mfrExpanded = effectiveExpandedMfr.has(mfr)
            const mfrCount = Object.values(models).flat().length

            return (
              <div key={mfr} className="mb-1">
                {/* Manufacturer row */}
                <button
                  onClick={() => toggleMfr(mfr)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${mfrExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="font-semibold text-gray-800 text-sm flex-1">{mfr}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{mfrCount}</span>
                </button>

                {/* Models */}
                {mfrExpanded && Object.entries(models).map(([model, regs]) => {
                  const modelKey = `${mfr}__${model}`
                  const modelExpanded = effectiveExpandedModel.has(modelKey)

                  return (
                    <div key={model} className="ml-6">
                      <button
                        onClick={() => toggleModel(modelKey)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${modelExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span className="text-sm text-gray-700 font-medium flex-1">{model}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{regs.length}</span>
                      </button>

                      {/* Registrations */}
                      {modelExpanded && regs.map(a => {
                        const regExpanded = expandedReg.has(a.id)
                        return (
                          <div key={a.id} className="ml-6">
                            <button
                              onClick={() => toggleReg(a.id)}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                            >
                              <svg className={`w-3 h-3 text-gray-300 transition-transform flex-shrink-0 ${regExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <div className="w-2 h-2 rounded-full flex-shrink-0 bg-gray-300" />
                              <span className="text-sm font-bold text-gray-800 tracking-wide flex-1">{a.registration}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                a.aircraft_type === 'Piston' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {a.aircraft_type || 'Jet'}
                              </span>
                            </button>

                            {/* Inline details */}
                            {regExpanded && (
                              <div className="ml-8 mb-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                {a.photo_path && (
                                  <img
                                    src={supabase.storage.from('aircraft-photos').getPublicUrl(a.photo_path).data.publicUrl}
                                    alt={a.registration}
                                    className="w-full h-28 object-cover rounded-lg mb-3"
                                  />
                                )}
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                  <div>
                                    <div className="text-xs text-gray-400">Registration</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.registration}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400">Type</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.aircraft_type || 'Jet'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400">Manufacturer</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.manufacturer || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400">Model</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.model || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400">Serial #</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.serial_number || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-400">Customer</div>
                                    <div className="text-sm font-semibold text-gray-800">{a.customer || '—'}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEdit(a)}
                                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-brand-red hover:bg-brand-red-dark text-white rounded-lg transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAircraft(a.id)}
                                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>

      {showModal && (
        <AircraftModal
          inline={asPage}
          aircraft={editAircraft}
          onClose={() => { setShowModal(false); setEditAircraft(null) }}
          onSaved={refetch}
          existingAircraft={aircraft}
        />
      )}
    </>
  )

  if (asPage) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {content}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {content}
      </div>
    </div>
  )
}